import React, { useMemo, useState } from "react";
import axios from "axios";
import { loginUser } from "../services/api";
import { setAuthToken } from "../services/authSession";

type FieldErrors = {
  identifier?: string;
  password?: string;
};

type ForgotState = "idle" | "sending" | "sent";

function looksLikeEmail(value: string): boolean {
  return value.includes("@");
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export default function LoginPage() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotState, setForgotState] = useState<ForgotState>("idle");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [authError, setAuthError] = useState<string | null>(null);

  const forgotHint = useMemo(() => {
    if (forgotState === "sent") {
      return "Wenn ein Konto existiert, wurden die Reset-Anweisungen gesendet.";
    }
    return null;
  }, [forgotState]);

  function validateForm(): boolean {
    const nextErrors: FieldErrors = {};
    const trimmedIdentifier = identifier.trim();

    if (!trimmedIdentifier) {
      nextErrors.identifier = "Bitte Benutzername oder E-Mail eingeben.";
    } else if (looksLikeEmail(trimmedIdentifier) && !isValidEmail(trimmedIdentifier)) {
      nextErrors.identifier = "Bitte eine gültige E-Mail-Adresse eingeben.";
    }

    if (!password) {
      nextErrors.password = "Bitte Passwort eingeben.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (loading) return;

    setAuthError(null);
    setForgotState("idle");

    if (!validateForm()) return;

    setLoading(true);
    try {
      const token = await loginUser(identifier.trim(), password);
      setAuthToken(token.access_token, {
        tokenType: token.token_type,
        username: identifier.trim(),
        rememberMe,
      });

      window.location.hash = "#/dashboard";
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 401) {
          setAuthError("Ungültige Zugangsdaten. Bitte prüfe Benutzername/E-Mail und Passwort.");
        } else {
          setAuthError(error.response?.data?.detail || "Anmeldung fehlgeschlagen. Bitte erneut versuchen.");
        }
      } else {
        setAuthError("Anmeldung fehlgeschlagen. Bitte erneut versuchen.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function onForgotPassword(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    setAuthError(null);

    const trimmedIdentifier = identifier.trim();
    if (!trimmedIdentifier) {
      setErrors((current) => ({
        ...current,
        identifier: "Für Passwort-Reset bitte zuerst Benutzername oder E-Mail eingeben.",
      }));
      return;
    }

    if (looksLikeEmail(trimmedIdentifier) && !isValidEmail(trimmedIdentifier)) {
      setErrors((current) => ({
        ...current,
        identifier: "Bitte eine gültige E-Mail-Adresse für den Reset eingeben.",
      }));
      return;
    }

    setErrors((current) => ({ ...current, identifier: undefined }));
    setForgotState("sending");

    await new Promise((resolve) => {
      window.setTimeout(resolve, 650);
    });

    setForgotState("sent");
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4 pt-16 pb-12">
      <div className="mx-auto w-full max-w-5xl">
        <div className="grid gap-10 rounded-3xl border border-indigo-100 bg-white/90 p-6 shadow-2xl backdrop-blur-sm md:grid-cols-2 md:p-10">
          <section className="order-2 md:order-1 md:pr-6">
            <p className="inline-flex rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">
              LinkyShorty Plattformzugang
            </p>
            <h1 className="mt-4 text-3xl font-bold text-gray-900 sm:text-4xl">
              Willkommen zurück
            </h1>
            <p className="mt-3 text-gray-600">
              Melde dich an, um Links zu verwalten, Klicks auszuwerten und deine Kampagnen zentral zu steuern.
            </p>

            <div className="mt-8 space-y-4">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm font-semibold text-gray-900">Schneller Zugriff</p>
                <p className="mt-1 text-sm text-gray-600">Nutze bestehende Kontodaten für sofortigen Zugriff auf dein Dashboard.</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm font-semibold text-gray-900">Sicheres Login</p>
                <p className="mt-1 text-sm text-gray-600">Passwörter werden verdeckt erfasst und nur sicher übertragen.</p>
              </div>
            </div>
          </section>

          <section className="order-1 md:order-2">
            <form onSubmit={onSubmit} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900">Anmelden</h2>
              <p className="mt-1 text-sm text-gray-600">Benutzername oder E-Mail und Passwort eingeben.</p>

              <div className="mt-6 space-y-5">
                <div>
                  <label htmlFor="identifier" className="mb-2 block text-sm font-medium text-gray-700">
                    Benutzername oder E-Mail
                  </label>
                  <input
                    id="identifier"
                    name="identifier"
                    type="text"
                    autoComplete="username"
                    value={identifier}
                    onChange={(event) => {
                      setIdentifier(event.target.value);
                      setErrors((current) => ({ ...current, identifier: undefined }));
                      setAuthError(null);
                    }}
                    className={`w-full rounded-xl border px-4 py-4 sm:py-3 text-gray-900 text-base outline-none transition-all ${
                      errors.identifier
                        ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                        : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    }`}
                    placeholder="name@firma.de oder username"
                    aria-invalid={Boolean(errors.identifier)}
                    aria-describedby={errors.identifier ? "identifier-error" : undefined}
                  />
                  {errors.identifier && (
                    <p id="identifier-error" role="alert" className="mt-2 text-sm text-red-700">
                      {errors.identifier}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="mb-2 block text-sm font-medium text-gray-700">
                    Passwort
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setErrors((current) => ({ ...current, password: undefined }));
                      setAuthError(null);
                    }}
                    className={`w-full rounded-xl border px-4 py-4 sm:py-3 text-gray-900 text-base outline-none transition-all ${
                      errors.password
                        ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                        : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    }`}
                    placeholder="Passwort"
                    aria-invalid={Boolean(errors.password)}
                    aria-describedby={errors.password ? "password-error" : undefined}
                  />
                  {errors.password && (
                    <p id="password-error" role="alert" className="mt-2 text-sm text-red-700">
                      {errors.password}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(event) => setRememberMe(event.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Angemeldet bleiben
                </label>
                <button
                  type="button"
                  onClick={onForgotPassword}
                  disabled={forgotState === "sending"}
                  className="text-sm font-medium text-blue-700 hover:text-blue-800 disabled:opacity-60"
                >
                  {forgotState === "sending" ? "Reset-Link wird gesendet..." : "Passwort vergessen?"}
                </button>
              </div>

              {forgotHint && (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                  {forgotHint}
                </div>
              )}

              {authError && (
                <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {authError}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="mt-6 w-full rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 font-medium text-white transition-all hover:from-blue-700 hover:to-purple-700 disabled:cursor-not-allowed disabled:from-gray-300 disabled:to-gray-300"
              >
                {loading ? "Melde an..." : "Anmelden"}
              </button>

              <p className="mt-4 text-center text-sm text-gray-600">
                Noch kein Konto?{" "}
                <a href="#/register" className="font-semibold text-blue-700 hover:text-blue-800">
                  Jetzt registrieren
                </a>
              </p>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}
